import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../config';

// ✅ Move type declaration here after imports
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface UserData {
  username: string;
  email: string;
  mobile: string;
}

const ContestRegistration: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const contest = location.state?.contest;
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);
  useEffect(() => {

    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/login");
      return;
    }

    fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setUserData(data.user);
        } else {
          localStorage.clear();
          navigate("/login");
        }
      })
      .catch(() => {
        localStorage.clear();
        navigate("/login");
      });
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 1️⃣ Check if user already registered
      const checkRes = await fetch(`${API_BASE_URL}/auth/check`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({ contest_id: contest.contest_id }),
      });

      const checkData = await checkRes.json();

      if (checkData.alreadyRegistered) {
        Swal.fire({
          title: "Info",
          text: "You have already registered for this contest.",
          icon: "info",
          confirmButtonText: "OK",
          customClass: {
            confirmButton: "bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700 focus:outline-none",
          },
          buttonsStyling: false,
        });
        setIsSubmitting(false);
        return;
      }

      // 2️⃣ Create Razorpay order
      const orderRes = await fetch(`${API_BASE_URL}/payment/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ amount: parseInt(contest.entry_fee) }),
      });

      const orderData = await orderRes.json();

      if (!orderData.id) {
        throw new Error("Failed to create Razorpay order");
      }

      // 3️⃣ Open Razorpay
      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: "INR",
        name: "Quizora",
        description: contest.contest_name,
        order_id: orderData.id,
        handler: async (response: any) => {
          Swal.fire({
            title: "Processing your registration...",
            text: "Please wait while we confirm your payment and generate your hall ticket.",
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          const registerRes = await fetch(`${API_BASE_URL}/contestRegistration/contest/register`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
            body: JSON.stringify({
              contest_id: contest.contest_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });

          const result = await registerRes.json();
          Swal.close();

          if (!registerRes.ok) {
            Swal.fire({
              title: "Error",
              text: result.error || "Registration failed",
              icon: "error",
              confirmButtonText: "OK",
              customClass: {
                confirmButton: "bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700",
              },
              buttonsStyling: false,
            });
          } else {
            Swal.fire({
              title: "Success",
              text: "Registration successful. Hall ticket has been emailed.",
              icon: "success",
              confirmButtonText: "Ok",
              customClass: {
                confirmButton: "bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700",
              },
              buttonsStyling: false,
            }).then(() => navigate("/"));
          }
        },
        prefill: {
          name: userData?.username,
          email: userData?.email,
          contact: userData?.mobile,
        },
        theme: { color: "#6366f1" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: "Error",
        text: "Something went wrong",
        icon: "error",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700",
        },
        buttonsStyling: false,
      });

    } finally {
      setIsSubmitting(false);
    }
  };

  if (!contest) {
    return <div className="text-center mt-16 text-red-500">No contest data found</div>;
  }

  if (!userData) {
    return <div className="text-center mt-16">Loading...</div>;
  }

  return (
    <div className="mt-20 max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-center text-indigo-600 mb-6">Contest Registration</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-gray-700">Name</label>
          <input
            type="text"
            value={userData.username}
            disabled
            className="w-full px-4 py-2 border rounded bg-gray-100"
          />
        </div>
        <div>
          <label className="block text-gray-700">Email</label>
          <input
            type="email"
            value={userData.email}
            disabled
            className="w-full px-4 py-2 border rounded bg-gray-100"
          />
        </div>
        <div>
          <label className="block text-gray-700">Mobile</label>
          <input
            type="text"
            value={userData.mobile}
            disabled
            className="w-full px-4 py-2 border rounded bg-gray-100"
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700"
        >
          {isSubmitting ? "Registering..." : "Pay & Register"}
        </button>
      </form>
    </div>
  );
};

export default ContestRegistration;
